/**
 * Vial labels: pick a product, the lot picks itself, choose how many, press Print.
 *
 * The person at the bench never types a lot number. When a product has one current lot it
 * is chosen for them and shown; when it has more than one they have to choose, and the
 * choice shows the size and the analysis date so the right one is obvious. The preview
 * card mirrors what the printer will put on the label, from the same fields.
 */
import { useCallback, useMemo, useState } from 'react';
import { Button, Card, EmptyState, ErrorState, Pill, SelectField, Skeleton, useToast } from '@/components';
import { ApiProblem } from '@/lib/api';
import { ulid } from '@/lib/ulid';
import { AgentPill } from './AgentPill';
import { JobList } from './JobList';
import {
  VIAL_QTY_DEFAULT,
  VIAL_QTY_MAX,
  VIAL_QTY_MIN,
  clampQty,
  qrTarget,
} from './labels';
import { useEnqueueVialJob, useVialJobs, useVialProducts } from './queries';
import type { VialLot, VialProduct } from './types';

const CHOOSE = '';

function lotLabel(lot: VialLot): string {
  const date = lot.analysis_date ? `, tested ${lot.analysis_date}` : '';
  return `${lot.batch} (${lot.content}${date})`;
}

export function VialLabels() {
  const products = useVialProducts();
  const jobs = useVialJobs();
  const enqueue = useEnqueueVialJob();
  const toast = useToast();

  const [productId, setProductId] = useState<string>(CHOOSE);
  const [coaId, setCoaId] = useState<string>(CHOOSE);
  const [qty, setQty] = useState<number>(VIAL_QTY_DEFAULT);
  const [problem, setProblem] = useState<string | null>(null);

  const list: readonly VialProduct[] = products.data?.products ?? [];
  const product = list.find((p) => String(p.product_id) === productId) ?? null;
  const lots = product?.lots ?? [];
  const lot = lots.find((l) => String(l.coa_id) === coaId) ?? null;

  const chooseProduct = useCallback(
    (nextId: string) => {
      setProductId(nextId);
      setProblem(null);
      const next = list.find((p) => String(p.product_id) === nextId);
      // One current lot: it is the lot. Several: the person chooses, on purpose.
      setCoaId(next && next.lots.length === 1 ? String(next.lots[0]?.coa_id ?? CHOOSE) : CHOOSE);
    },
    [list],
  );

  const productOptions = useMemo(
    () => [
      { value: CHOOSE, label: 'Choose a product' },
      ...list.map((p) => ({ value: String(p.product_id), label: p.label_name })),
    ],
    [list],
  );

  const lotOptions = useMemo(
    () => [
      { value: CHOOSE, label: 'Choose the lot' },
      ...lots.map((l) => ({ value: String(l.coa_id), label: lotLabel(l) })),
    ],
    [lots],
  );

  const canPrint = product !== null && lot !== null && !enqueue.isPending;

  async function print(): Promise<void> {
    if (!product || !lot) return;
    setProblem(null);
    // One key per press, reused by the client across its own retry, never regenerated.
    const idempotencyKey = ulid();
    try {
      await enqueue.mutateAsync({ coa_id: lot.coa_id, qty, idempotencyKey });
      toast.push({
        tone: 'ok',
        message: `Queued ${qty} ${product.label_name} ${lot.content} labels, lot ${lot.batch}.`,
      });
    } catch (error) {
      if (error instanceof ApiProblem) {
        setProblem(
          error.shortRequestId ? `${error.message} (request ${error.shortRequestId})` : error.message,
        );
      } else {
        setProblem('The print did not queue. Try again.');
      }
    }
  }

  const agent = jobs.data?.agent ?? products.data?.agent ?? null;

  return (
    <Card title="Vial labels" action={<AgentPill agent={agent} />} id="bench-vial">
      {products.isLoading ? <Skeleton shape="card" /> : null}

      {products.isError ? (
        <ErrorState
          message="The product list did not load."
          requestId={products.error instanceof ApiProblem ? products.error.shortRequestId : null}
          onRetry={() => void products.refetch()}
        />
      ) : null}

      {products.data && list.length === 0 ? (
        <EmptyState message="No product has a current certificate yet, so there is nothing to print." />
      ) : null}

      {list.length > 0 ? (
        <div className="bench-vial">
          <div className="bench-vial__form">
            <SelectField
              label="Product"
              options={productOptions}
              value={productId}
              onChange={(event) => chooseProduct(event.target.value)}
            />

            {product && lots.length === 1 && lot ? (
              <div className="o-field">
                <span className="o-field__label">Lot</span>
                <p className="bench-lot" data-testid="bench-lot-auto">
                  <span className="o-mono">{lot.batch}</span>
                  <span className="o-muted">
                    {' '}
                    {lot.content}
                    {lot.analysis_date ? `, tested ${lot.analysis_date}` : ''}
                  </span>
                </p>
                <span className="o-field__hint">The only current lot, chosen for you.</span>
              </div>
            ) : null}

            {product && lots.length > 1 ? (
              <SelectField
                label="Lot"
                hint="This product has more than one current lot. Pick the one on the vials in front of you."
                options={lotOptions}
                value={coaId}
                onChange={(event) => {
                  setCoaId(event.target.value);
                  setProblem(null);
                }}
              />
            ) : null}

            <div className="o-field">
              <label className="o-field__label" htmlFor="bench-qty">
                How many
              </label>
              <div className="bench-qty">
                <Button
                  aria-label="Fewer labels"
                  onClick={() => setQty((current) => clampQty(current - 1))}
                  disabled={qty <= VIAL_QTY_MIN}
                >
                  -
                </Button>
                <input
                  id="bench-qty"
                  className="o-input bench-qty__input"
                  type="number"
                  inputMode="numeric"
                  min={VIAL_QTY_MIN}
                  max={VIAL_QTY_MAX}
                  value={qty}
                  onChange={(event) => setQty(clampQty(Number(event.target.value)))}
                />
                <Button
                  aria-label="More labels"
                  onClick={() => setQty((current) => clampQty(current + 1))}
                  disabled={qty >= VIAL_QTY_MAX}
                >
                  +
                </Button>
              </div>
              <span className="o-field__hint" data-testid="bench-qty-hint">
                Up to {VIAL_QTY_MAX} at a time. The printer prints exactly this many.
              </span>
            </div>

            {problem ? (
              <p className="bench-problem" role="alert">
                {problem}
              </p>
            ) : null}

            <Button variant="primary" className="bench-print" busy={enqueue.isPending} disabled={!canPrint} onClick={() => void print()}>
              {lot ? `Print ${qty} labels` : 'Print'}
            </Button>
          </div>

          <LabelPreview product={product} lot={lot} />
        </div>
      ) : null}

      <JobList jobs={jobs} />
    </Card>
  );
}

/** What the label will say, from the same fields the agent prints. */
function LabelPreview({ product, lot }: { product: VialProduct | null; lot: VialLot | null }) {
  if (!product) {
    return (
      <div className="bench-preview bench-preview--empty" aria-label="Label preview">
        <p className="o-muted">Choose a product to see the label.</p>
      </div>
    );
  }
  return (
    <div className="bench-preview" aria-label="Label preview" data-testid="bench-preview">
      <div className="bench-preview__name">{product.label_name}</div>
      {product.blend ? <div className="bench-preview__blend">{product.blend}</div> : null}
      <div className="bench-preview__content">{lot ? lot.content : 'size'}</div>
      <div className="bench-preview__lot">
        <span className="o-muted">Lot </span>
        <span className="o-mono">{lot ? lot.batch : 'choose a lot'}</span>
      </div>
      <div className="bench-preview__qr">
        <span className="bench-preview__qrbox" aria-hidden="true" />
        <span className="o-mono bench-preview__qrtext">{lot ? qrTarget(lot.batch) : ''}</span>
      </div>
      {lot && !lot.is_current ? <Pill tone="warn">Not the current lot</Pill> : null}
    </div>
  );
}
